import { Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { Dialog, DialogContent, Input, cn } from '@aeris/ui'

import type { ModuleEntry } from '../lib/modules.tsx'
import { useVisibleModules } from '../lib/use-visible-modules.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Ctrl/Cmd+K — go to any screen by name.
 *
 * The sidebar answers "what is there"; this answers "take me to X", which is
 * the question somebody who already knows the product asks all day and which a
 * menu answers badly, because it costs a scan of everything to find one thing.
 *
 * It searches the description as well as the label on purpose. "cliente" is not
 * the name of any screen and it is what somebody types looking for Pessoas.
 *
 * The list is local and complete — every screen the product has, already in
 * memory — so this filters in the browser. That is the opposite of Combobox,
 * which never filters because its options come from the server.
 */

const normalise = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    // Strip diacritics, so "orcamento" finds "orçamento" and "precos" finds
    // "Preços". Nobody reaches for the accent key mid-shortcut.
    .replace(/\p{Diacritic}/gu, '')

const Results = ({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void
}): ReactNode => {
  const workspace = useWorkspace()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const listRef = useRef<HTMLUListElement>(null)

  // As abas entram: a busca acha "custos" mesmo que Custos de atendimento não
  // ocupe mais linha no menu (F068). A regra é uma só para todo lugar (F138).
  const available = useVisibleModules({ includeTabs: true })

  const results = useMemo(() => {
    const terms = normalise(query).split(/\s+/).filter(Boolean)
    if (terms.length === 0) return available

    return available.filter((item) => {
      const haystack = normalise(`${item.label} ${item.description}`)
      return terms.every((term) => haystack.includes(term))
    })
  }, [available, query])

  useEffect(() => {
    const row = listRef.current?.querySelector('[data-active="true"]')
    // Guarded: jsdom has no layout, so it does not implement scrollIntoView.
    if (
      row instanceof HTMLElement &&
      typeof row.scrollIntoView === 'function'
    ) {
      row.scrollIntoView({ block: 'nearest' })
    }
  }, [active, results])

  /**
   * `newInstance` com Shift (F138): no Modo PC abre OUTRA janela da tela; no
   * Modo Site não há segunda superfície e o Shift não muda nada.
   */
  const go = (item: ModuleEntry, newInstance = false): void => {
    onOpenChange(false)
    workspace.open({ module: item.module, newInstance })
  }

  return (
    <>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-(--text-muted)"
        />
        <Input
          autoFocus
          role="combobox"
          aria-expanded
          aria-controls="module-search-results"
          aria-activedescendant={
            results[active] === undefined
              ? undefined
              : `module-search-${results[active].module}`
          }
          aria-label="Buscar tela"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            // A filtered list whose highlight stayed on row 7 would send
            // Enter somewhere the person never looked at.
            setActive(0)
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              setActive(
                (current) => (current + 1) % Math.max(1, results.length)
              )
            } else if (event.key === 'ArrowUp') {
              event.preventDefault()
              setActive(
                (current) =>
                  (current - 1 + results.length) % Math.max(1, results.length)
              )
            } else if (event.key === 'Home') {
              event.preventDefault()
              setActive(0)
            } else if (event.key === 'End') {
              event.preventDefault()
              setActive(Math.max(0, results.length - 1))
            } else if (event.key === 'Enter') {
              event.preventDefault()
              const chosen = results[active]
              if (chosen !== undefined) go(chosen, event.shiftKey)
            }
          }}
          placeholder="Ex.: preços, cliente, caixa…"
          className="pl-8"
        />
      </div>

      <ul
        ref={listRef}
        id="module-search-results"
        role="listbox"
        aria-label="Telas"
        className="mt-2 flex max-h-80 flex-col gap-0.5 overflow-y-auto"
      >
        {results.length === 0 ? (
          <li className="px-2 py-6 text-center text-(length:--text-sm) text-(--text-muted)">
            Nenhuma tela com esse nome.
          </li>
        ) : (
          results.map((item, index) => (
            <li key={item.module}>
              <button
                type="button"
                id={`module-search-${item.module}`}
                role="option"
                aria-selected={index === active}
                data-active={index === active}
                // Pointer down rather than click: the input holds focus and a
                // click would blur it first, closing on the way past.
                onMouseDown={(event) => {
                  event.preventDefault()
                  go(item, event.shiftKey)
                }}
                onMouseEnter={() => {
                  setActive(index)
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-(--radius-md) px-2 py-1.5 text-left',
                  'text-(length:--text-sm) text-(--text-primary)',
                  index === active && 'bg-(--surface-hover)'
                )}
              >
                <item.Icon
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-(--text-secondary)"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{item.label}</span>
                  <span className="block truncate text-(length:--text-xs) text-(--text-muted)">
                    {item.description}
                  </span>
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
    </>
  )
}

export const ModuleSearch = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}): ReactNode => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    {/* Mounted only while open, so the query and the highlight start clean
        every time rather than being reset by an effect watching `open`. */}
    {open ? (
      <DialogContent
        title="Ir para"
        description="Digite o nome da tela ou o que você quer fazer."
        className="max-w-xl"
      >
        <Results onOpenChange={onOpenChange} />
      </DialogContent>
    ) : null}
  </Dialog>
)
